import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertMessageService, LoadingService } from '@serpro/ngx-suite-rfb';
import { Observable, forkJoin } from 'rxjs';
import { appRoute } from 'src/app/shared/consts/app-routes';
import { Anotacao } from 'src/app/shared/model/anotacao.model';
import { ArquivoBinario } from 'src/app/shared/model/arquivo-binario.model';
import { Assunto } from 'src/app/shared/model/assunto.model';
import { AtoComparacao } from 'src/app/shared/model/ato-comparacao.model';
import { AtoConsulta } from 'src/app/shared/model/ato-consulta.model';
import { Ato } from 'src/app/shared/model/ato.model';
import { Orgao } from 'src/app/shared/model/orgao.model';
import { SegmentoTela } from 'src/app/shared/model/segmento.model';
import { TipoAto } from 'src/app/shared/model/tipo-ato.model';
import { TipoEdicao, CORRECAO_ATO, CURADORIA_ATO, DUPLICACAO_ATO, INCLUSAO_ATO, REPUBLICACAO_ATO, REPUBLICACAO_ATO_COMPARACAO } from 'src/app/shared/model/tipo-edicao.model';
import { TipoSegmento } from 'src/app/shared/model/tipo-segmento.model';
import { AnotacaoService } from 'src/app/shared/services/anotacao.service';
import { AssuntoService } from 'src/app/shared/services/assunto.service';
import { AtoService } from 'src/app/shared/services/ato.service';
import { CompartilhamentoDadosService } from 'src/app/shared/services/compartilhamento-dados.service';
import { OrgaoService } from 'src/app/shared/services/orgao.service';
import { TipoAtoService } from 'src/app/shared/services/tipo-ato.service';
import { TipoSegmentoService } from 'src/app/shared/services/tipo-segmento.service';
import { Base64 } from 'src/app/shared/util/base64.util';
import { DataUtil } from 'src/app/shared/util/data.util';
import { SegmentoTelaUtil } from 'src/app/shared/util/segmento-tela.util';

/**
 * Este componente prepara a exibição do formulário de edição de ato para as funcionalidades:
 *  - incluir-ato
 *  - incluir-por-curadoria
 *  - incluir-por-duplicacao
 *  - corrigir-ato
 *  - republicar-ato
 * 
 * Sua função é tirar complexidade do FormularioAtoComponent. Por exemplo:
 *  - definindo a operação a ser executada a partir do endereço de chamada (route)
 *  - preparando a variável do tipo Ato (a partir de dados do banco, por exemplo)
 *  - alimentando as coleções necessárias para a interface do formulário
 *  - aplicar o método correto de salvamento
 *  - exibir a mensagem adequada e direcionar o fluxo de execução (rota)
 * 
 * Para uma demanda futura, poderia implementar navegação entre atos distintos nesta mesma página.
 */
@Component({
    selector: 'app-editar-ato',
    templateUrl: './editar-ato.component.html',
    styleUrls: ['./editar-ato.component.css', '../../../../../src/app/shared/css/contraste.css']
})
export class EditarAtoComponent implements OnInit {

    public exibirFormulario = false;

    public ato: Ato;
    public tipoEdicao: TipoEdicao;

    public tiposAto: TipoAto[];
    public orgaos: Orgao[];
    public assuntos: Assunto[];
    public tiposSegmento: TipoSegmento[];
    public anotacoes: Anotacao[];
    public segmentosTela: SegmentoTela[];

    idAtoOriginalRepublicacao: number | undefined;
    sucessoRepublicacao = false;

    atosDuplicados: AtoConsulta[] = [];

    constructor(
        private route: ActivatedRoute,
        private loadingService: LoadingService,
        private alertMessageService: AlertMessageService,
        private atoService: AtoService,
        private anotacaoService: AnotacaoService,
        private router: Router,
        private tipoAtoService: TipoAtoService,
        private orgaoService: OrgaoService,
        private tipoSegmentoService: TipoSegmentoService,
        private compartilhamentoDadosService: CompartilhamentoDadosService,
        private assuntoService: AssuntoService,
    ) { }

    ngOnInit(): void {

        this.alertMessageService.dismissAll();
        this.loadingService.show();

        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        let sources: Observable<any>[] = [];
        sources = [
            this.tipoAtoService.getAtivos(),
            this.orgaoService.getAtivos(),
            this.tipoSegmentoService.findAll(),
            this.assuntoService.listar(),
        ];

        const paramMap = this.route.snapshot.paramMap;
        const idStr = paramMap.get('id');

        const path = this.route.snapshot.routeConfig?.path;
        let idDetalheInclusaoManual = 0;
        if (path) {
            if (path.indexOf('incluir-ato') >= 0) {
                if (idStr == null) {
                    this.tipoEdicao = INCLUSAO_ATO;
                    document.title = 'Incluir novo ato';
                }
            } else {
                if (idStr != null) {
                    const id = parseInt(idStr);
                    idDetalheInclusaoManual = id;
                    if (path.indexOf('corrigir-ato') >= 0) {
                        this.tipoEdicao = CORRECAO_ATO;
                        sources.push(this.atoService.findById(id));
                        sources.push(this.anotacaoService.buscarAnotacoesDoAto(id));
                        document.title = 'Corrigir ' + id;
                    } else if (path.indexOf('incluir-por-curadoria') >= 0) {
                        sources.push(this.atoService.buscarDetalheParaEdicao(id));
                        this.tipoEdicao = CURADORIA_ATO;
                        document.title = 'Incluir por curadoria';
                    } else if (path.indexOf('incluir-por-duplicacao') >= 0) {
                        this.tipoEdicao = DUPLICACAO_ATO;
                        sources.push(this.atoService.buscarDetalheParaEdicao(id));
                        document.title = 'Incluir por duplicação';
                    } else if (path.indexOf('republicar-ato') >= 0) {
                        this.tipoEdicao = REPUBLICACAO_ATO; 
                        document.title = 'Republicar ' + id;
                        if (!this.sucessoRepublicacao) {
                            sources.push(this.atoService.findById(id)); 
                        }
                    }
                } else {
                    if (path.indexOf('republicar-comparacao') >= 0) {
                        this.tipoEdicao = REPUBLICACAO_ATO_COMPARACAO;
                        const dadosCompartilhados = this.compartilhamentoDadosService.getData("repub-comparacao");
                        if (!dadosCompartilhados || !dadosCompartilhados.idAtoOriginalRepublicacao) {
                            this.loadingService.hide();
                            this.router.navigate([`${appRoute.ato.incluir}`]); 
                        } else {
                            const idAtoOriginalRepublicacao = dadosCompartilhados.idAtoOriginalRepublicacao;
                            document.title = 'Republicar ' + idAtoOriginalRepublicacao;
                            sources.push(this.atoService.findById(idAtoOriginalRepublicacao));
                        }
                    }
                }
            }
        }

        forkJoin(sources).subscribe({
            next: (retorno) => {

                this.tiposAto = retorno[0];
                this.tiposAto.sort((a, b) => a.nomeTipoAto > b.nomeTipoAto ? 1 : -1);

                this.orgaos = retorno[1];
                this.orgaos.sort((a, b) => a.siglaOrgao > b.siglaOrgao ? 1 : -1);

                this.tiposSegmento = retorno[2];

                this.assuntos = retorno[3];

                if (this.tipoEdicao.isInclusaoAto()) {
                    // para o incluir-ato, é criado um Ato vazio.
                    document.title = 'Incluir ato';
                    this.ato = this.criarAtoNovo();
                } else {

                    // para as outras funcionalidades há um quinto retorno com um Ato ou um Detalhe de importação
                    const ato: Ato = (retorno[4] as Ato);

                    if (ato === null) {
                        this.alertMessageService.error('Erro', 'Não foi encontrado ato com o ID informado.');
                        this.loadingService.hide();
                        return;
                    }

                    // Verifica se algum orgao do ato não está na lista de orgaos ativos
                    this.verificarOrgaosInativos(ato);

                    // Caso o ato tenha um tipo inativo, adiciona ele na lista
                    this.verificarTipoAtoInativo(ato);

                    if (this.tipoEdicao.isCorrecaoAto()) {
                        // para o corrigir-ato, há um sexto retorno com as anotações do ato
                        this.anotacoes = retorno[5] as Anotacao[];
                    }

                    if (this.tipoEdicao.isRepublicacaoAto()) {

                        if (ato.republicacao && !this.sucessoRepublicacao) {
                            this.alertMessageService.error('Erro', 'Este ato não pode ser republicado.');
                            this.loadingService.hide();
                            return;
                        }

                        this.idAtoOriginalRepublicacao = ato.idAto;
                        ato.idAto = undefined;
                        ato.dataPublicacao = DataUtil.getDataNDiasAtrasComHoraZerada(0);
                        ato.dataVigenciaInicio = DataUtil.getDataNDiasAtrasComHoraZerada(0);
                        ato.segmentos = [];
                        ato.secao = '';
                        ato.numeroPagina = undefined;
                        ato.internet = false;
                        ato.publicado = false;
                        ato.dataVigenciaFim = undefined;
                        ato.idLocalPublicacao = 1;
                        ato.exibirVisoes = '1111';
                    }

                    if (this.tipoEdicao.isRepublicacaoAtoComparacao()) {

                        if (this.sucessoRepublicacao) {
                            this.loadingService.hide();
                            return;
                        }

                        if (ato.republicacao && !this.sucessoRepublicacao) {
                            this.alertMessageService.error('Erro', 'Este ato não pode ser republicado.');
                            this.loadingService.hide();
                            return;
                        }
    
                        this.atualizarDadosDoCompartilhamento(ato);
                    }

                    if (this.tipoEdicao.isCuradoriaAto()) {
                        if (ato.situacaoProcessamentoAto !== 'CURADORIA') {
                            // Somente podemos abrir a funcionalidade de curadoria se o Detalhe de importação ainda estiver em "Curadoria"
                            this.alertMessageService.warn("Alerta", "Este ato já foi retirado da curadoria por inclusão manual.");
                            this.loadingService.hide();
                            return;
                        }

                        // Em curadoria é sempre "Permitir" exibição na internet
                        ato.internet = true;
                        ato.idDetalheInclusaoManual = idDetalheInclusaoManual;
                    }

                    if (this.tipoEdicao.isDuplicacaoAto()) {
                        ato.idAto = undefined;
                        ato.internet = true;
                    }

                    if (!ato.dataAto) {
                        // se é null, precisa ser undefined pra não exibir erro no DatePicker
                        ato.dataAto = undefined;
                    }

                    // Original | Multivigente | Vigente | Relacional
                    // ato.exibirVisoes = '1111'; // deve vir do banco - não inicializar
                    if (!ato.exibirVisoes) {
                        ato.exibirVisoes = '1111';
                    }

                    if (!ato.idLocalPublicacao) {
                        ato.idLocalPublicacao = 1; // DOU
                    }

                    // campo auxiliar com os arquivos anexos. É alimentado ao salvar
                    // ato.files = [];

                    this.criarSegmentosTela(ato);
                    this.ato = ato;
                }
                this.loadingService.hide();
                this.exibirFormulario = true;
                setTimeout(() => {
                    // foco em uma div invisível e tabindex=0 para não dar foco em nada
                    document.getElementById('invisivel')?.focus();
                 }, 200);
            },
            error: () => this.loadingService.hide()
        });
    }

    private verificarTipoAtoInativo(ato: Ato) {
        
        if (ato.tipoAto) {
            let reordenarTipos = false;

            const tipoAtoAtivoEncontrado = this.tiposAto.find(t => t.idTipoAto === ato.tipoAto.idTipoAto);

            if (!tipoAtoAtivoEncontrado) {
                // Tipo Ato Inativo
                reordenarTipos = true;
                const novoTipoAto = new TipoAto();
                novoTipoAto.idTipoAto = ato.tipoAto.idTipoAto;
                novoTipoAto.nomeTipoAto = ato.tipoAto.nomeTipoAto;
                novoTipoAto.siglaTipoAto = ato.tipoAto.siglaTipoAto;
                novoTipoAto.prioridadeTipoAto = ato.tipoAto.prioridadeTipoAto;
                this.tiposAto.push(novoTipoAto);
            }

            if (reordenarTipos) {
                this.tiposAto.sort((a, b) => a.nomeTipoAto > b.nomeTipoAto ? 1 : -1);
            }
        }
    }

    private verificarOrgaosInativos(ato: Ato) {
        
        if (ato.orgaos) {
            let reordenarOrgaos = false;

            ato.orgaos.forEach(oAto => {
                const orgaoAtivoEncontrado = this.orgaos.find(oAtivo => oAtivo.idOrgao === oAto.idOrgao);

                if (!orgaoAtivoEncontrado) {
                    reordenarOrgaos = true;
                    const novoOrgaoInativo = new Orgao();
                    novoOrgaoInativo.idOrgao = oAto.idOrgao;
                    novoOrgaoInativo.nomeOrgao = oAto.nomeOrgao;
                    novoOrgaoInativo.siglaOrgao = oAto.siglaOrgao;
                    novoOrgaoInativo.prioridadeOrgao = oAto.prioridadeOrgao;
                    this.orgaos.push(novoOrgaoInativo);
                }
            });

            if (reordenarOrgaos) {
                this.orgaos.sort((a, b) => a.siglaOrgao > b.siglaOrgao ? 1 : -1);
            }
        }
    }

    private criarAtoNovo(): Ato {
        this.segmentosTela = [];
        const ato = new Ato();
        ato.dataPublicacao = DataUtil.getDataNDiasAtrasComHoraZerada(0);
        ato.dataVigenciaInicio = DataUtil.getDataNDiasAtrasComHoraZerada(0);
        const tipoAtoDefault = this.tiposAto.find(t => t.nomeTipoAto === 'Ato Declaratório Executivo');
        if (tipoAtoDefault) {
            ato.tipoAto = tipoAtoDefault;
        }
        return ato;
    }

    /**
     * Ajusta o ato em caso de republicação a partir da comparação de atos.
     * Copia para o ato recuperado da base de dados (atoBase) os dados alterados na comparação de atos (atoComparacao.ato);
     */
    private atualizarDadosDoCompartilhamento(atoBase: Ato) {

        const atoComparacao: AtoComparacao = this.compartilhamentoDadosService.getData("repub-comparacao");
        this.idAtoOriginalRepublicacao = atoComparacao.idAtoOriginalRepublicacao;

        atoBase.idAto = undefined;
        atoBase.dataPublicacao = atoComparacao.ato.dataPublicacao;
        atoBase.idLocalPublicacao = atoComparacao.ato.idLocalPublicacao;
        atoBase.secao = atoComparacao.ato.secao;
        atoBase.numeroPagina = atoComparacao.ato.numeroPagina;
        atoBase.textoLivre = atoComparacao.ato.textoLivre;
        atoBase.publicado = atoComparacao.ato.publicado;
        atoBase.dataVigenciaInicio = atoComparacao.ato.dataVigenciaInicio;
        atoBase.dataVigenciaFim = atoComparacao.ato.dataVigenciaFim;
        atoBase.internet = atoComparacao.ato.internet;
        atoBase.exibirVisoes = atoComparacao.ato.exibirVisoes;
        atoBase.orgaoResponsavel = atoComparacao.ato.orgaoResponsavel;
        atoBase.segmentos = [];
        this.segmentosTela = atoComparacao.segmentosTela;
    }

    public limparAto(flag: boolean) {
        if (flag) {
            this.exibirFormulario = false;
            this.tipoEdicao = INCLUSAO_ATO;
            this.ato = this.criarAtoNovo();
            setTimeout(() => {
                this.exibirFormulario =  true;
            }, 10);
        }
    }

    /**
     * A partir dos segmentos do ato, cria os segmentos de tela, com informações necessárias ao funcionamento do editor de conteúdo.
     */
    private criarSegmentosTela(ato: Ato) {

        // Em caso de Republicacao da Comparacao, os segmentos da tela já foram definidos.
        if (this.tipoEdicao.isRepublicacaoAtoComparacao()) {
            return;
        }

        this.segmentosTela = [];
        for (const segmentoAto of ato.segmentos) {
            const segmento = SegmentoTelaUtil.criarDoSegmento(segmentoAto);

            
            if (this.tipoEdicao.isCorrecaoAto()) {
                segmento.original = segmentoAto.original;
                segmento.compilado = segmentoAto.compilado;
            }
            if (segmentoAto.idTipoSegmento === 16 || segmentoAto.arquivoBinario) {
                segmento.arquivoBinario = segmentoAto.arquivoBinario;
                // ATENÇÃO: quando for EDIÇÃO é necessário acatar o textoIntegra original ou o da obs
                if ([4,5].indexOf(segmentoAto.arquivoBinario?.idTipoArquivo) >= 0) {
                    segmento.innerHTML = Base64.decode(segmentoAto.arquivoBinario.arquivoBinario);
                }
            }
            this.segmentosTela.push(segmento);
        }
        if (this.tipoEdicao.isCorrecaoAto()) {
            this.categorizarSegmentosTachadosOuExcluiveis();
        }
    }

    /**
     * Na funcionalidade corrigir-ato, é necessário apresentar segmentos não vigentes como taxados.
     * Também é preciso sinalizar os segmentos que não podem ser excluídos por serem polos ativos ou passivos de anotações.
     */
    private categorizarSegmentosTachadosOuExcluiveis() {
        let segmentoAnterior: SegmentoTela | null = null;
        for (const segmento of this.segmentosTela) {
            for (const anotacao of this.anotacoes) {
                if (anotacao.segmentoOrigem.idSegmento == segmento.idSegmento
                        && anotacao.segmentoOrigem.versaoSegmento == segmento.versaoSegmento) {
                    segmento.anotacoesComoDestino.push(anotacao);
                    segmento.anotado = true;
                }
                if (anotacao.segmentoDestino.idSegmento == segmento.idSegmento
                        && anotacao.segmentoDestino.versaoSegmento == segmento.versaoSegmento) {
                    segmento.anotacoesComoOrigem.push(anotacao);
                    segmento.anotado = true;
                }
            }
            if (segmentoAnterior != null) {
                if (segmentoAnterior.idSegmento == segmento.idSegmento) {
                    segmentoAnterior.versionado = true;
                    segmento.versionado = true;
                }
            }
            segmentoAnterior = segmento;
        }
        for (const segmento of this.segmentosTela) {
            if (segmento.versionado) {
                segmento.anotado = false;
            }
        }
    }

    public salvarAto(ato: Ato) {

        if (ato != null) {
        
            for (const s of ato.segmentos) {
                s.textoIntegra = s.textoIntegra.replaceAll('\n', '');
                if (s.idTipoSegmento == 16 || s.arquivoBinario) {
                    const id = s.arquivoBinario.idArquivoBinario;
                    s.arquivoBinario = new ArquivoBinario();
                    s.arquivoBinario.idArquivoBinario = id;
                }
            }
            
            if (this.tipoEdicao.isCorrecaoAto()) {
                this.corrigirAto(ato);
                return;
            }

            if (this.tipoEdicao.isRepublicacaoAto() || this.tipoEdicao.isRepublicacaoAtoComparacao() ) {
                this.republicarAto(ato);
                return;
            }

            this.criarAto(ato);
        }
    }

    private criarAto(ato: Ato) {
        this.atoService.create(ato).subscribe({
            next: (retorno: string) => {
                this.loadingService.hide();
                this.alertMessageService.info('Sucesso', 'Ato salvo com sucesso com id ' + retorno + '.');
                this.limparAto(true);
            },
            error: () => this.loadingService.hide()
        });
    }

    private corrigirAto(ato: Ato) {
        this.atoService.corrigirAto(ato).subscribe({
            next: (retorno: string) => {
                this.loadingService.hide();
                this.exibirFormulario = false;
                this.ngOnInit();
                this.alertMessageService.info('Sucesso', 'Ato corrigido com sucesso com id ' + retorno + '.');
            },
            error: () => this.loadingService.hide()
        });
    }

    private republicarAto(ato: Ato) {
        this.atoService.republicarAto(this.idAtoOriginalRepublicacao, ato).subscribe({
            next: (retorno: string) => {
                this.loadingService.hide();
                this.exibirFormulario = false;
                this.sucessoRepublicacao = true;
                this.ngOnInit();
                this.alertMessageService.info('Sucesso', 'Ato republicado com sucesso com id ' + retorno + '.');
            },
            error: () => this.loadingService.hide()
        });
    }

    public fecharModalDuplicidade(fechar: boolean) {
        if (fechar) {
            this.atosDuplicados = [];
        }
    }
}
